import { briefFramingMarkers } from '@sigora/domain';
import { useEffect, useRef, useState } from 'react';

import {
  type BriefHistoryEntryView,
  type BriefVersionView as BriefVersionRead,
  fetchBriefVersion,
} from '../../api/client.ts';
import {
  BACK_TO_CURRENT_LABEL,
  BRIEF_LOADING,
  BRIEF_READ_FAILED,
  TRY_AGAIN_LABEL,
  briefVersionBanner,
} from '../brief-copy.ts';
import type { FollowUpScope } from '../follow-ups/FollowUpThread.tsx';
import { BriefBlocks } from './BriefBlocks.tsx';
import { revisionAsOf, revisionMarks } from './RevisionMarks.tsx';
import { briefDate } from './brief-date.ts';
import type { BriefReadCache } from './use-brief-read.ts';

/**
 * Past versions read so far, per study: a stored version never changes, so one read once opens
 * again at once. Keyed by the study's `BriefReadCache`, which its shell holds for exactly as long
 * as the study is open, so this goes with it. The versions held are one study's: a cache given
 * another study starts again with none.
 */
const versionCaches = new WeakMap<
  BriefReadCache,
  { studyId: string; versions: Map<number, BriefVersionRead> }
>();

const versionCacheOf = (cache: BriefReadCache, studyId: string): Map<number, BriefVersionRead> => {
  const existing = versionCaches.get(cache);
  if (existing?.studyId === studyId) {
    return existing.versions;
  }
  const created = new Map<number, BriefVersionRead>();
  versionCaches.set(cache, { studyId, versions: created });
  return created;
};

type VersionState =
  { status: 'loading' } | { status: 'failed' } | { status: 'ready'; read: BriefVersionRead };

/** One past version, read once per study and then served from the study's cache. */
const useBriefVersion = (
  studyId: string,
  version: number,
  cache: BriefReadCache,
): { state: VersionState; retry: () => void } => {
  const versions = versionCacheOf(cache, studyId);
  const held = versions.get(version);
  /** The read state, with the version it is for. */
  const [state, setState] = useState<{ version: number; read: VersionState }>({
    version,
    read: held === undefined ? { status: 'loading' } : { status: 'ready', read: held },
  });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const cached = versions.get(version);
    if (cached !== undefined) {
      setState({ version, read: { status: 'ready', read: cached } });
      return;
    }

    const controller = new AbortController();
    setState({ version, read: { status: 'loading' } });
    fetchBriefVersion(studyId, version, controller.signal).then(
      (read) => {
        versions.set(version, read);
        if (!controller.signal.aborted) {
          setState({ version, read: { status: 'ready', read } });
        }
      },
      () => {
        if (!controller.signal.aborted) {
          setState({ version, read: { status: 'failed' } });
        }
      },
    );

    return () => controller.abort();
  }, [studyId, version, versions, attempt]);

  // Until the effect has caught up with a newly opened version, the state is the last one's,
  // whether it was read or failed.
  const current = state.version === version ? state.read : { status: 'loading' as const };

  return { state: current, retry: () => setAttempt((count) => count + 1) };
};

/**
 * A past version of the Brief, opened from its history: drawn as the page showed it — that
 * version's own framing marks, and the "Updated" tags of Sigora's newest revision at or before it,
 * with the revision's note and framing lines when this is that revision — under a banner naming
 * the version and the date it was written, with the way back to the current Brief. While the
 * version is read, or after its read failed, the banner says so in place of the date. Nothing on it can be edited.
 * The banner takes the focus when a version opens, so a keyboard or screen reader lands on what
 * replaced the page.
 */
export const BriefVersionView = ({
  studyId,
  version,
  history,
  cache,
  onBack,
  onAsk,
}: {
  studyId: string;
  version: number;
  /** The current read's history, oldest first, which names the revision this version was marked with. */
  history: readonly BriefHistoryEntryView[];
  /** The study's Brief cache, which keys the versions read so far. */
  cache: BriefReadCache;
  onBack: () => void;
  /** Ask about this on a card of this version, which is the same card on the Brief as it stands. */
  onAsk: (card: FollowUpScope) => void;
}) => {
  const { state, retry } = useBriefVersion(studyId, version, cache);
  const banner = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    banner.current?.focus();
  }, [version]);

  return (
    <>
      <div
        className="brief-notice brief-version-banner"
        data-test-id="brief-version-banner"
        tabIndex={-1}
        ref={banner}
      >
        {state.status === 'ready' && (
          <p data-test-id="brief-version-banner-text">
            {briefVersionBanner(version, briefDate(state.read.createdAt))}
          </p>
        )}
        {state.status === 'loading' && <p data-test-id="brief-version-loading">{BRIEF_LOADING}</p>}
        {state.status === 'failed' && (
          <>
            <p className="chat-error" data-test-id="brief-version-failed" role="alert">
              {BRIEF_READ_FAILED}
            </p>
            <button type="button" data-test-id="brief-version-retry" onClick={retry}>
              {TRY_AGAIN_LABEL}
            </button>
          </>
        )}
        <button type="button" data-test-id="brief-version-back" onClick={onBack}>
          {BACK_TO_CURRENT_LABEL}
        </button>
      </div>
      {state.status === 'ready' && (
        <div data-test-id="brief-version-content" data-version={state.read.version}>
          <BriefBlocks
            content={state.read.content}
            markers={briefFramingMarkers(state.read.content)}
            marks={revisionMarks(revisionAsOf(history, state.read.version), state.read.version)}
            onAsk={onAsk}
          />
        </div>
      )}
    </>
  );
};
